import { Badge, Box, HStack, Text, Tooltip } from '@chakra-ui/react'
import { generatePath } from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import { Down, Up } from 'src/components/__legacy__/icon/monochrome'
import { MessageWarning } from 'src/components/icon'
import { LinkTable } from 'src/components/LinkTable/LinkTable'
import { Paths } from 'src/constants/paths'
import {
  useAuthenticatedAccount,
  usePermission,
} from 'src/features/auth/context'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import { PERMISSIONS } from 'src/lib/permission'
import { getFullName } from 'src/utils/getFullName'

import { SearchPatientsReturn, usePatients } from '../../api/searchPatients'
import { useSortPatients } from '../../hooks/useSortPatients'
import { getRelatedHospitalName } from '../../utils/getReleatedHospital'

const hasIncompleteWorksheet = (
  patient: SearchPatientsReturn['patients'][number],
) => {
  if (patient.status === 'Disable') return false
  return patient.statistics.incompleteWorksheetsCount > 0
}

export const PatientsTable: React.FC = () => {
  const { data } = usePatients({})
  const { sortKey, sortOrder } = useSortPatients()

  const { hasPermission } = usePermission()
  const {
    account: { selectedTrial, selectedTrialHospitalUid },
  } = useAuthenticatedAccount()

  const patients = data?.patients ?? []

  return (
    <LinkTable
      data={patients}
      columns={[
        { key: 'status', renderHeader: null },
        {
          key: 'diseaseId',
          renderHeader: (
            <HStack spacing="2" align="center">
              <Text as="span">症例番号</Text>
              {sortKey === 'DiseaseUID' &&
                (sortOrder === 'Desc' ? <Down /> : <Up />)}
            </HStack>
          ),
        },
        {
          key: 'name',
          isVisible: hasPermission(PERMISSIONS.Patient_ReadDetail),
          renderHeader: (
            <HStack>
              <Text as="span">氏名</Text>
              {sortKey === 'Name' && (sortOrder === 'Desc' ? <Down /> : <Up />)}
            </HStack>
          ),
        },
        {
          key: 'kana',
          isVisible: hasPermission(PERMISSIONS.Patient_ReadDetail),
          renderHeader: 'ふりがな',
        },
        {
          key: 'hospitalName',
          renderHeader: (
            <HStack spacing="2" align="center">
              <Text as="span">医療機関</Text>
              {sortKey === 'HospitalName' &&
                (sortOrder === 'Desc' ? <Down /> : <Up />)}
            </HStack>
          ),
          isVisible: hasPermission(PERMISSIONS.Hospital_BelongAll),
        },
        {
          key: 'relatedHospital',
          renderHeader: '関連施設',
        },
      ]}
      linkTarget="diseaseId"
      link={patient =>
        generatePath(Paths.Patient, {
          trialUid: selectedTrial.uid,
          patientUid: patient.uid,
        })
      }
      renderRow={patient => ({
        rowId: patient.uid,
        trProps: { opacity: patient.status === 'Disable' ? 0.3 : 1 },
        columnValues: {
          status: {
            renderColumn: (
              <HStack spacing="8px">
                {hasIncompleteWorksheet(patient) ? (
                  <Box pos="relative" zIndex="1">
                    <Tooltip label="未入力フィールドがあります。">
                      <Box as="span">
                        <MessageWarning
                          color={colors.yellow[500]}
                          size="12px"
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box w="12px" />
                )}
                {patient.status === 'Disable' ? (
                  <Badge
                    w="40px"
                    bg="gray.100"
                    color="gray.400"
                    px="2"
                    py="0.5"
                  >
                    無効
                  </Badge>
                ) : (
                  <Badge
                    w="40px"
                    bg="green.200"
                    color="green.700"
                    px="2"
                    py="0.5"
                  >
                    有効
                  </Badge>
                )}
              </HStack>
            ),
            tdProps: { w: '60px' },
          },
          diseaseId: {
            renderColumn: patient.diseaseUid,
            tdProps: { minW: '160px' },
          },
          name: {
            renderColumn: getFullName(patient),
            tdProps: { minW: '160px' },
          },
          kana: {
            renderColumn: `${patient.lastNameJa} ${patient.firstNameJa}`,
            tdProps: { minW: '160px' },
          },
          hospitalName: {
            renderColumn: patient.trialHospital.hospital.name,
            tdProps: {
              minW: '160px',
            },
          },
          relatedHospital: {
            renderColumn: getRelatedHospitalName(
              patient,
              selectedTrialHospitalUid,
            ),
            tdProps: { minW: '160px' },
          },
        },
      })}
    />
  )
}
